import React, { useContext } from 'react';
import {
  ContentManager,
  Product,
  CompareProductFieldsResponse,
  CompareProductField,
  ProductFilterOptions,
} from '@benzinga/content-manager';
import { SessionContext } from '@benzinga/session-context';
import { SelectableCompareTable } from './SelectableCompareTable';
import { ProductFilterSelect } from './ProductFilterSelect';
import styled from '@benzinga/themetron';

export interface SelectableCompareProductsSideBySideProps {
  data: {
    compare_products?: {
      products?: Product[];
    };
    active_filters?: string[];
    compare_custom_fields?: CompareProductFieldsResponse;
    filter_options?: ProductFilterOptions;
    vertical?: any;
    enable_user_selection?: boolean;
    pre_filter?: {
      country?: string;
    };
    filters?: {
      country?: boolean;
      account_size?: boolean;
      asset_type?: boolean;
    };
    enable_product_selection?: boolean;
    compare_prod_title?: string;
    compare_prod_desc?: string;
    products_to_show?: number;
  };
}

interface ProductFilter {
  country?: string;
  account_size?: string;
  asset_type?: string;
  limit?: number;
}

export const SelectableCompareProductsSideBySide: React.FC<SelectableCompareProductsSideBySideProps> = ({ data }) => {
  const session = useContext(SessionContext);

  const compareProducts = data?.compare_products?.products ?? [];
  const customFields = data?.compare_custom_fields?.fields ?? [];
  const vertical = data?.vertical[0];
  const productsToShow = data?.products_to_show ?? 3;
  const initialCompareProduct =
    compareProducts && compareProducts.length > 0 ? compareProducts.slice(0, productsToShow) : [];

  const preAppliedFilter = data?.pre_filter;

  const initialFilters = {
    limit: productsToShow,
  };
  if (data?.active_filters && data?.active_filters.length > 0) {
    data?.active_filters?.map(filter => {
      initialFilters[filter] = '';
    });
  }

  if (preAppliedFilter) {
    Object.keys(preAppliedFilter).map(filter => {
      if (preAppliedFilter[filter] != '') {
        initialFilters[filter] = preAppliedFilter[filter];
      }
    });
  }

  const [currentComparingProducts, setCurrentComparingProducts] = React.useState<Product[]>(initialCompareProduct);
  const [productList, setProductList] = React.useState<Product[] | []>(compareProducts);
  const [compareCustomFields, setCompareCustomFields] = React.useState<CompareProductField[] | []>(customFields);
  const [enableProductSelection, setEnableProductSelection] = React.useState<boolean>(
    data?.enable_product_selection ?? false,
  );
  const [loadingData, setLoadingData] = React.useState<boolean>(false);

  const [appliedFilters, setAppliedFilters] = React.useState<ProductFilter>(initialFilters);

  const onChangeHandleSelectedProduct = React.useCallback(
    (itemId, index) => {
      const newSelectedProduct = productList.find(o => o.id == itemId);
      if (newSelectedProduct) {
        const newState = [...currentComparingProducts];
        newState[index] = newSelectedProduct;
        setCurrentComparingProducts(newState);
      }
    },
    [productList, currentComparingProducts],
  );

  const isAllFiltersApplied = filters => {
    let allFiltersApplied = true;
    Object.keys(filters).map(filter => {
      if (filters[filter] == '') {
        allFiltersApplied = false;
      }
    });
    return allFiltersApplied;
  };

  const onChangeGetProductData = React.useCallback(
    async (value: string, item: string) => {
      const updatedFilters = appliedFilters;
      updatedFilters[item] = value;
      setAppliedFilters(updatedFilters);

      if (isAllFiltersApplied(updatedFilters)) {
        setLoadingData(true);
        const contentManager = await session.getManager(ContentManager);
        const response = await contentManager.getProduct({
          ...updatedFilters,
          vertical: vertical,
        });

        if (response.ok) {
          const products = response.ok?.products;
          const defaultProducts = products?.length > 0 ? products.slice(0, productsToShow) : [];
          setCurrentComparingProducts(defaultProducts);
          setProductList(products);
        }
        const fieldResponse = await contentManager.getCustomFieldsLabel({
          vertical: vertical,
        });
        if (fieldResponse?.ok && fieldResponse.ok?.fields) {
          setCompareCustomFields(fieldResponse.ok.fields);
        }
        setLoadingData(false);
        setEnableProductSelection(true);
      }
    },
    [appliedFilters, session, vertical, productsToShow],
  );

  return (
    <ProductSelectionWrap>
      <div className="compare-product-wrap my-4 shadow-sm bg-white border border-t-4 p-4">
        <h3>{data?.compare_prod_title && data.compare_prod_title}</h3>
        <div>{data?.compare_prod_desc && data.compare_prod_desc}</div>
        {data?.active_filters && data?.active_filters.length > 0 && (
          <ProductFilterSelect
            filterOptions={data?.filter_options}
            loadingData={loadingData}
            onChangeGetProductData={onChangeGetProductData}
            preAppliedFilter={data?.pre_filter}
            productFilter={data?.filters}
          />
        )}
      </div>

      {enableProductSelection &&
        (productList.length > 0 ? (
          <SelectableCompareTable
            compareCustomFields={compareCustomFields}
            currentSelection={currentComparingProducts}
            onChangeHandleSelectedProduct={onChangeHandleSelectedProduct}
            productList={productList}
            productsToShow={productsToShow}
          />
        ) : (
          <span>No Products Found :/</span>
        ))}
    </ProductSelectionWrap>
  );
};

export const ProductSelectionWrap = styled.div`
  display: block;
  width: 100%;
  .compare-product-wrap {
    border-top-color: #ff5a1f;
    h3 {
    }
  }
`;
